import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
  type DocumentData,
  type QuerySnapshot,
} from 'firebase/firestore';
import {
  addDays,
  dayOf,
  dayStart,
  DEFAULT_ZONE,
  isRecord,
  knownZone,
  readCourier,
  readHandIn,
  readPresence,
  readRouteMoney,
  type Courier,
  type Doc,
  type HandIn,
  type Presence,
  type RouteMoney,
} from '@day';
import { db } from '../firebase';
import { createStore } from '../app/store';

/**
 * What the owner's control room reads, live: every query is one the rules allow an owner (firestore.rules), filtered
 * on their own uid. Routes come from four queries, as the phone's Live reads them (live_provider.dart): started today,
 * still running, ended today, and sent but not started. The day is the business's (`where.timeZone`).
 */

/**
 * Where the business's routes start (`businesses/{id}.places`, ProfileService.saveBusinessPlaces): no position of
 * Google's is kept. [pin] is the owner's own, from a plus code or coordinates they gave (control/plus-code): kept as it is.
 */
export interface BusinessPlace {
  label: string;
  address: string;
  placeId?: string;
  pin?: { lat: number; lng: number };
}

/** A real point the owner gave, from what the record holds; null otherwise. */
const pinOfPlace = (raw: unknown): { lat: number; lng: number } | null =>
  isRecord(raw) && typeof raw.lat === 'number' && typeof raw.lng === 'number' && Math.abs(raw.lat) <= 90 && Math.abs(raw.lng) <= 180 ? { lat: raw.lat, lng: raw.lng } : null;

/**
 * What the owner set for one of their couriers (`businesses/{id}.couriers.{uid}`, courier_settings.dart): one of the six
 * colours by its place, the short name the business knows them by, and paused (with when, kept for billing).
 */
export interface CourierSet {
  colour?: number;
  name?: string;
  paused: boolean;
  pausedAt?: number;
}

/** The longest short name (CourierSettings.maxName): it has to fit beside a disc. */
export const SHORT_NAME_MAX = 24;

/** A short name as it's kept: trimmed, single spaces, at most [SHORT_NAME_MAX]; null when nothing's left. */
export function cleanShortName(raw: string | null | undefined): string | null {
  const t = (raw ?? '').trim().replace(/\s+/g, ' ');
  return t ? t.slice(0, SHORT_NAME_MAX).trim() : null;
}

/** Every courier's, from the business's record; what can't be read is left out. */
export function readCourierSets(raw: unknown): Record<string, CourierSet> {
  const out: Record<string, CourierSet> = {};
  if (!isRecord(raw)) return out;
  for (const [uid, v] of Object.entries(raw)) {
    if (!isRecord(v)) continue;
    const at = v.pausedAt as { toMillis?: () => number } | number | undefined;
    out[uid] = {
      ...(Number.isInteger(v.colour) && (v.colour as number) >= 0 && (v.colour as number) < 6 ? { colour: v.colour as number } : {}),
      ...(typeof v.name === 'string' && cleanShortName(v.name) ? { name: cleanShortName(v.name)! } : {}),
      paused: v.paused === true,
      ...(typeof at === 'number' ? { pausedAt: at } : at?.toMillis ? { pausedAt: at.toMillis() } : {}),
    };
  }
  return out;
}

export interface Business {
  id: string;
  name: string;
  /** Null: never set. Empty: the owner removed it. */
  places: BusinessPlace[] | null;
  logoUrl: string | null;
  /** The zone its day runs in; [zoneConfirmed] false when the owner hasn't said and this computer's is used. */
  zone: string;
  zoneConfirmed: boolean;
  country: string | null;
  payments: {
    on: boolean;
    /** The owner has said, on or off (Finish setting up's "Payment at the door"). */
    chosen: boolean;
    currency: string;
    onCallByDefault: boolean;
    couriers: Record<string, boolean>;
  };
  /** Each courier's colour, short name and pause, as the owner set them. */
  couriers: Record<string, CourierSet>;
}

export interface SentStop {
  id: string;
  riderId: string;
  kind: string;
  status: string;
  holder: string | null;
  holderId: string | null;
  fromRouteId: string | null;
  fromStopId: string | null;
  sentAt: number | null;
  data: Doc;
}

export interface Data {
  status: 'idle' | 'loading' | 'ready' | 'error';
  error: string | null;
  uid: string | null;
  business: Business | null;
  couriers: Courier[];
  /** The owner's own rider record: their name in the top bar. */
  ownerName: string | null;
  /** Route records by id, from all four queries. */
  routes: Map<string, Doc>;
  sentStops: SentStop[];
  presence: Presence[];
  routeMoney: RouteMoney[];
  handIns: HandIn[];
  /** Alerts cleared today, and routes still open cleared on any day. */
  cleared: Set<string>;
  /** The day the queries were made for. */
  day: string | null;
  /** The business's routes from the last 60 days (Reports, the couriers' rates, yesterday by now); read once a day. */
  history: Map<string, Doc> | null;
  /** The server's records of the same days (functions/src/daily_totals.ts), by day; read once a day, empty if unreadable. */
  days: Map<string, Doc> | null;
  /** Stops the business has decided about (`stopDecisions`: left, given, closed), as `routeId/stopId`. */
  decided: Set<string>;
  /** Stops waiting for a route (`businesses/{id}/waitingStops`, docs/waiting-stops.md), by id, as stored. */
  waiting: Map<string, Doc>;
  /** The waiting stops couldn't be read: their rule isn't deployed yet. */
  waitingUnreadable: boolean;
  /** The owner's notifications (`notifications/{uid}/items`, functions/src/notices.ts), newest first. */
  notes: Note[];
  /** The zones the owner drew (`businesses/{id}/zones`, src/data/zones.ts), by id, as stored. */
  zones: Map<string, Doc>;
  /** The zones couldn't be read: their rule isn't deployed yet. */
  zonesUnreadable: boolean;
}

export interface Note {
  id: string;
  kind: string;
  title: string;
  body: string;
  read: boolean;
  at: number | null;
  /** What it's about (a route, a stop, a courier), when the server said. */
  data: Doc;
}

export const data = createStore<Data>({
  status: 'idle',
  error: null,
  uid: null,
  business: null,
  couriers: [],
  ownerName: null,
  routes: new Map(),
  sentStops: [],
  presence: [],
  routeMoney: [],
  handIns: [],
  cleared: new Set(),
  day: null,
  history: null,
  days: null,
  decided: new Set(),
  waiting: new Map(),
  waitingUnreadable: false,
  notes: [],
  zones: new Map(),
  zonesUnreadable: false,
});

const browserZone = () => {
  const z = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return knownZone(z) ? z : DEFAULT_ZONE;
};

export function readBusiness(id: string, d: Doc | undefined): Business {
  const where = isRecord(d?.where) ? d!.where : null;
  const confirmed = !!where && knownZone(where.timeZone);
  const p = isRecord(d?.payments) ? d!.payments : {};
  const couriers: Record<string, boolean> = {};
  if (isRecord(p.couriers)) for (const [k, v] of Object.entries(p.couriers)) if (v === 'onCall' || v === 'employed') couriers[k] = v === 'onCall';
  return {
    id,
    name: typeof d?.name === 'string' && d.name.trim() ? d.name.trim() : 'Your business',
    places: Array.isArray(d?.places)
      ? (d!.places as unknown[]).filter(isRecord).flatMap((x) =>
          typeof x.address === 'string' && x.address.trim()
            ? [
                {
                  label: typeof x.label === 'string' && x.label.trim() ? x.label.trim() : 'Shop',
                  address: x.address.trim(),
                  ...(typeof x.placeId === 'string' && x.placeId ? { placeId: x.placeId } : {}),
                  ...(pinOfPlace(x.pin) ? { pin: pinOfPlace(x.pin)! } : {}),
                },
              ]
            : [],
        )
      : null,
    logoUrl: typeof d?.logoUrl === 'string' && d.logoUrl ? d.logoUrl : null,
    zone: confirmed ? (where!.timeZone as string) : browserZone(),
    zoneConfirmed: confirmed,
    country: where && typeof where.country === 'string' ? where.country : null,
    payments: {
      on: p.on !== false,
      chosen: typeof p.on === 'boolean',
      currency: typeof p.currency === 'string' && p.currency.length === 3 ? p.currency : 'NGN',
      onCallByDefault: p.onCall === true,
      couriers,
    },
    couriers: readCourierSets(d?.couriers),
  };
}

/** A stop the business sent one at a time (`sentStops/{id}`, SentStop.fromFirestore). */
export function readSentStop(id: string, x: Doc): SentStop {
  const str = (v: unknown) => (typeof v === 'string' ? v : null);
  return {
    id,
    riderId: String(x.riderId ?? ''),
    kind: String(x.kind ?? ''),
    status: String(x.status ?? 'sent'),
    holder: str(x.holder),
    holderId: str(x.holderId),
    fromRouteId: str(x.fromRouteId),
    fromStopId: str(x.fromStopId),
    sentAt: (x.sentAt as Timestamp | undefined)?.toMillis?.() ?? (typeof x.sentAt === 'number' ? x.sentAt : null),
    data: x,
  };
}

/** Whether a courier keeps their delivery fees (`PaymentSettings.onCall`). */
export const onCallOf = (b: Business | null) => (uid: string) => b?.payments.couriers[uid] ?? b?.payments.onCallByDefault ?? false;

/** How far back the past days go: two 30-day periods, for Reports' "than the 30 days before". */
export const HISTORY_DAYS = 60;

let stops: (() => void)[] = [];
let dayTimer: ReturnType<typeof setInterval> | null = null;

function stopAll() {
  for (const s of stops) s();
  stops = [];
  if (dayTimer) clearInterval(dayTimer);
  dayTimer = null;
}

const failed = (e: unknown) => data.set({ status: 'error', error: e instanceof Error ? e.message : String(e) });

/** Starts reading the owner's records; stops what was there before. */
export function watch(uid: string) {
  stopAll();
  data.set({ status: 'loading', error: null, uid, routes: new Map(), day: null, history: null, days: null });

  // The business first: its zone sets the day the route queries start from.
  let dayStops: (() => void)[] = [];
  const startDay = () => {
    const b = data.get().business;
    if (!b) return;
    const today = dayOf(Date.now(), b.zone);
    if (data.get().day === today && dayStops.length) return;
    for (const s of dayStops) s();
    const midnight = Timestamp.fromMillis(dayStart(today, b.zone));
    const sources = new Map<string, Map<string, Doc>>();
    const merge = (name: string) => (snap: QuerySnapshot<DocumentData>) => {
      sources.set(name, new Map(snap.docs.map((d) => [d.id, d.data() as Doc])));
      // A route can come through several queries; the newest copy wins (they're the same document).
      const all = new Map<string, Doc>();
      for (const m of sources.values()) for (const [id, d] of m) all.set(id, d);
      data.set({ routes: all, status: sources.size >= 5 ? 'ready' : data.get().status });
    };
    // A query whose index may not be deployed yet: without it, the day carries on from the others.
    const quiet = (name: string) => (e: unknown) => {
      console.warn(`[live] ${name}`, e);
      merge(name)({ docs: [] } as unknown as QuerySnapshot<DocumentData>);
    };
    const routes = collection(db, 'routes');
    const mine = where('businessId', '==', uid);
    dayStops = [
      onSnapshot(query(routes, mine, where('startedAt', '>=', midnight)), merge('today'), failed),
      onSnapshot(query(routes, mine, where('status', '==', 'active')), merge('running'), failed),
      onSnapshot(query(routes, mine, where('endedAt', '>=', midnight)), merge('ended'), failed),
      // Started before midnight and finished today: not in the others, but its stops handled today are today's numbers.
      onSnapshot(query(routes, mine, where('completedAt', '>=', midnight)), merge('completed'), quiet('completed')),
      onSnapshot(query(routes, mine, where('status', 'in', ['sent', 'declined'])), merge('sent'), failed),
      onSnapshot(
        query(collection(db, 'businesses', uid, 'clearedAlerts'), where('day', '==', today)),
        (s) => setCleared('today', s),
        failed,
      ),
    ];
    data.set({ day: today });
    // The past days: read once for the day, not watched (they don't change).
    routesSince(uid, dayStart(addDays(today, -HISTORY_DAYS), b.zone)).then(
      (h) => data.get().uid === uid && data.set({ history: h }),
      failed,
    );
    // The server's records of the days, watched: a day's last stops arrive with the next sweep. Without them (not
    // written yet, or the rule not live), every day is worked out from the routes.
    dayStops.push(
      onSnapshot(
        query(collection(db, 'businesses', uid, 'days'), where('day', '>=', addDays(today, -HISTORY_DAYS))),
        (snap) => data.set({ days: new Map(snap.docs.map((d) => [d.id, d.data() as Doc])) }),
        () => data.set({ days: new Map() }),
      ),
    );
  };

  const cleared = new Map<string, Set<string>>();
  const setCleared = (name: string, s: QuerySnapshot<DocumentData>) => {
    // Records from before the day was in the id are just the alert's id (LiveAlertsService._alertOf).
    cleared.set(name, new Set(s.docs.map((d) => d.id.split('|').at(-1)!)));
    data.set({ cleared: new Set([...cleared.values()].flatMap((x) => [...x])) });
  };

  stops.push(
    // The owner's notifications, as the phone lists them.
    onSnapshot(
      query(collection(db, 'notifications', uid, 'items'), orderBy('createdAt', 'desc'), limit(50)),
      (s) =>
        data.set({
          notes: s.docs.map((d) => {
            const x = d.data() as Doc;
            return { id: d.id, kind: String(x.kind ?? ''), title: String(x.title ?? ''), body: String(x.body ?? ''), read: x.read === true, at: (x.createdAt as Timestamp | undefined)?.toMillis?.() ?? null, data: (x.data && typeof x.data === 'object' ? x.data : {}) as Doc };
          }),
        }),
      () => data.set({ notes: [] }),
    ),
    // Stops waiting for a route: only the owner's, under their business.
    onSnapshot(
      collection(db, 'businesses', uid, 'waitingStops'),
      (s) => data.set({ waiting: new Map(s.docs.map((d) => [d.id, d.data() as Doc])), waitingUnreadable: false }),
      (e) => {
        console.warn('[live] waitingStops', e);
        data.set({ waiting: new Map(), waitingUnreadable: true });
      },
    ),
    // The zones the owner drew: only theirs, under their business; unreadable until their rule is deployed.
    onSnapshot(
      collection(db, 'businesses', uid, 'zones'),
      (s) => data.set({ zones: new Map(s.docs.map((d) => [d.id, d.data() as Doc])), zonesUnreadable: false }),
      (e) => {
        console.warn('[live] zones', e);
        data.set({ zones: new Map(), zonesUnreadable: true });
      },
    ),
    // What the business decided about stops not delivered: read only; unreadable until its rule is deployed, so empty then.
    onSnapshot(
      collection(db, 'businesses', uid, 'stopDecisions'),
      (s) =>
        data.set({
          decided: new Set(
            s.docs.flatMap((d) => {
              const x = d.data();
              return typeof x.routeId === 'string' && typeof x.stopId === 'string' ? [`${x.routeId}/${x.stopId}`] : [];
            }),
          ),
        }),
      () => data.set({ decided: new Set() }),
    ),
    onSnapshot(
      doc(db, 'businesses', uid),
      (s) => {
        data.set({ business: readBusiness(uid, s.data() as Doc | undefined) });
        startDay();
      },
      failed,
    ),
    onSnapshot(
      query(collection(db, 'riders'), where('businessId', '==', uid)),
      (s) =>
        data.set({
          couriers: s.docs.filter((d) => d.id !== uid).map((d) => readCourier(d.id, d.data() as Doc)),
          ownerName: (s.docs.find((d) => d.id === uid)?.data().name as string | undefined) ?? null,
        }),
      failed,
    ),
    onSnapshot(
      query(collection(db, 'sentStops'), where('businessId', '==', uid)),
      (s) =>
        data.set({
          sentStops: s.docs.map((d) => readSentStop(d.id, d.data() as Doc)),
        }),
      failed,
    ),
    onSnapshot(
      query(collection(db, 'presence'), where('businessId', '==', uid)),
      (s) => data.set({ presence: s.docs.map((d) => readPresence(d.id, d.data() as Doc)).filter((p): p is Presence => !!p) }),
      failed,
    ),
    onSnapshot(
      query(collection(db, 'routeMoney'), where('businessId', '==', uid)),
      (s) => data.set({ routeMoney: s.docs.map((d) => readRouteMoney(d.id, d.data() as Doc)).filter((r): r is RouteMoney => !!r) }),
      failed,
    ),
    onSnapshot(
      query(collection(db, 'handIns'), where('businessId', '==', uid)),
      (s) => data.set({ handIns: s.docs.map((d) => readHandIn(d.id, d.data() as Doc)).filter((h): h is HandIn => !!h) }),
      failed,
    ),
    onSnapshot(
      query(collection(db, 'businesses', uid, 'clearedAlerts'), where('kind', '==', 'stillOpen')),
      (s) => setCleared('open', s),
      failed,
    ),
    () => {
      for (const s of dayStops) s();
      dayStops = [];
    },
  );
  // Midnight: the day's queries start again from the new day.
  dayTimer = setInterval(startDay, 60_000);
}

export function unwatch() {
  stopAll();
  data.set({ status: 'idle', uid: null, business: null, couriers: [], routes: new Map(), sentStops: [], presence: [], routeMoney: [], handIns: [], cleared: new Set(), day: null, history: null, days: null, decided: new Set(), waiting: new Map(), waitingUnreadable: false, notes: [], zones: new Map(), zonesUnreadable: false });
}

/** The business's finished routes since [from] (Reports), read once: past days don't change. */
export async function routesSince(uid: string, from: number): Promise<Map<string, Doc>> {
  const snap = await getDocs(query(collection(db, 'routes'), where('businessId', '==', uid), where('startedAt', '>=', Timestamp.fromMillis(from))));
  return new Map(snap.docs.map((d) => [d.id, d.data() as Doc]));
}
